/**
 * ADR-173 D1/D5 — the contract-term fields, configured ONCE.
 *
 * Every surface that captures a contract's terms (Hire, Rehire, Add Contract)
 * or changes one (Amend Contract) reads its labels, helper texts and picker
 * configuration from here, so the same field reads and behaves the same
 * everywhere. Change a label or a picker option here, not in a dialog.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const CONTRACT_TERM_LABELS = Object.freeze({
  contractType: 'Contract Type',
  contractCompany: 'Contract Company',
  engagementModel: 'Engagement Model',
  contractNumber: 'Contract Number',
  contractStartDate: 'Contract Start Date',
  contractEndDate: 'Contract End Date',
  fixedTermComment: 'Fixed-term comment'
});

export const CONTRACT_TERM_HELPER_TEXTS = Object.freeze({
  contractNumber: "Optional: the contract's number in the system of record.",
  contractEndDateOptional: 'Optional: set for a fixed-term contract; leave empty for an open-ended one.',
  fixedTermComment: "Auto-generated from the term; edit freely. Saved as the episode comment and the scheduled end's reason.",
  fixedTermCommentRequired: 'Required when a contract end date is set'
});

/**
 * ADR-142 §8 + ADR-173 F2 — the company picker everywhere: the AJAR mode
 * (every company listed; a cross-Region pick warns, never hides) with the
 * country and company-type chips, so same-named entities can be told apart.
 * Callers add `warnClusterId` (the individual's Region).
 */
export const CONTRACT_COMPANY_SELECTOR_PROPS = Object.freeze({
  showCountry: true,
  includeGlobalCompanies: true
});

/**
 * The problem with an end date, or null. Exported so a surface can gate its
 * submit on exactly what the field shows.
 */
export const fixedTermEndProblem = ({ startYmd, endYmd, minEndYmd = null, maxEndYmd = null, startLabel = 'contract start date' }) => {
  if (!endYmd) {
    return null;
  }
  if (startYmd && endYmd <= startYmd) {
    return `Contract end date must be after the ${startLabel}`;
  }
  if (minEndYmd && endYmd < minEndYmd) {
    return `Must be on or after ${dayjs.utc(minEndYmd).format('DD/MM/YYYY')}`;
  }
  if (maxEndYmd && endYmd > maxEndYmd) {
    return `Must be on or before ${dayjs.utc(maxEndYmd).format('DD/MM/YYYY')}`;
  }
  return null;
};
